import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { getLicenses, generateLicense, revokeLicense, getLicensePlans, createLicensePlan, getPosDownloads, createPosDownload, getBusiness, getLicenseMgmtDashboard, getCatalogProducts } from '../../services/api';
import BusinessSearchSelect from '../../components/BusinessSearchSelect';
import { KeyRound, Plus, Copy, Ban, Store, Monitor, Search, X, ChevronDown, ChevronUp, Download, Upload, Settings, Receipt, AlertTriangle, Package, Layers } from 'lucide-react';
import { toast } from 'sonner';
import AddonCatalogTab from './licencias/AddonCatalogTab';
import StampPackagesTab from './licencias/StampPackagesTab';

const STATUS_LABELS = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  activated: { label: 'Activada', color: 'bg-blue-100 text-blue-700' },
  linked: { label: 'Ligada', color: 'bg-green-100 text-green-700' },
  expired: { label: 'Expirada', color: 'bg-slate-100 text-slate-600' },
  revoked: { label: 'Revocada', color: 'bg-red-100 text-red-700' },
};

const POS_TYPE_LABELS = {
  retail: 'Retail',
  restaurant: 'Restaurante',
};

const LicenciasPOS = () => {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [businessCache, setBusinessCache] = useState({});
  const [filterBusinessName, setFilterBusinessName] = useState('');
  const [generateBusinessName, setGenerateBusinessName] = useState('');
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [softwareProducts, setSoftwareProducts] = useState([]);
  const [filters, setFilters] = useState({ business_id: '', status: '', pos_type: '', product_id: '' });
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [activeTab, setActiveTab] = useState('licenses');
  const [generateForm, setGenerateForm] = useState({ business_id: '', plan_id: '', pos_type: 'retail', product_id: '', notes: '' });
  const [planForm, setPlanForm] = useState({ name: '', code: '', pos_type: 'retail', description: '', price_mxn: 0, billing_cycle: 'perpetual', max_terminals: 1, features: [] });
  const [downloadForm, setDownloadForm] = useState({ pos_type: 'retail', version: '', platform: 'windows', download_url: '', file_size_mb: '', release_notes: '', min_os_version: 'Windows 10', is_latest: true });
  const [stampsDashboard, setStampsDashboard] = useState(null);
  const [stampsLoading, setStampsLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadLicenses();
  }, [filters]);

  const loadData = async () => {
    try {
      const [plansRes, downloadsRes, productsRes] = await Promise.all([
        getLicensePlans(),
        getPosDownloads().catch(() => ({ data: [] })),
        getCatalogProducts().catch(() => ({ data: [] })),
      ]);
      setPlans(plansRes.data);
      setDownloads(downloadsRes.data);
      setSoftwareProducts(productsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadLicenses = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.business_id) params.business_id = filters.business_id;
      if (filters.status) params.status = filters.status;
      if (filters.pos_type) params.pos_type = filters.pos_type;
      if (filters.product_id) params.product_id = filters.product_id;
      const response = await getLicenses(params);
      setLicenses(response.data.licenses || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading licenses:', error);
      toast.error('Error al cargar licencias');
    } finally {
      setLoading(false);
    }
  };

  const loadStampsDashboard = async () => {
    setStampsLoading(true);
    try {
      const res = await getLicenseMgmtDashboard();
      setStampsDashboard(res.data);
    } catch (error) {
      console.error('Error loading stamps dashboard:', error);
    } finally {
      setStampsLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      const response = await generateLicense(generateForm);
      toast.success(`Serial generado: ${response.data.serial_number}`);
      setShowGenerateModal(false);
      setGenerateForm({ business_id: '', plan_id: '', pos_type: 'retail', product_id: '', notes: '' });
      loadLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar licencia');
    }
  };

  const handleRevoke = async () => {
    if (!showRevokeModal) return;
    try {
      await revokeLicense(showRevokeModal.id, revokeReason);
      toast.success('Licencia revocada');
      setShowRevokeModal(null);
      setRevokeReason('');
      loadLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al revocar');
    }
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    try {
      await createLicensePlan(planForm);
      toast.success('Plan creado');
      setShowPlanModal(false);
      setPlanForm({ name: '', code: '', pos_type: 'retail', description: '', price_mxn: 0, billing_cycle: 'perpetual', features: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear plan');
    }
  };

  const handleCreateDownload = async (e) => {
    e.preventDefault();
    try {
      const data = { ...downloadForm, file_size_mb: downloadForm.file_size_mb ? parseFloat(downloadForm.file_size_mb) : null };
      await createPosDownload(data);
      toast.success('Versión registrada');
      setShowDownloadModal(false);
      setDownloadForm({ pos_type: 'retail', version: '', platform: 'windows', download_url: '', file_size_mb: '', release_notes: '', min_os_version: 'Windows 10', is_latest: true });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar versión');
    }
  };

  const copySerial = (serial) => {
    navigator.clipboard.writeText(serial);
    toast.success('Serial copiado al portapapeles');
  };

  // Resolve business names from cache or fetch
  const getBusinessName = (id) => {
    if (!id) return '—';
    if (businessCache[id]) return businessCache[id];
    // Fetch async and cache
    getBusiness(id).then((res) => {
      setBusinessCache((prev) => ({ ...prev, [id]: res.data.name }));
    }).catch(() => {});
    return id.slice(0, 8) + '...';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  if (loading && licenses.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Licencias POS</h1>
            <p className="text-slate-600 mt-2">Gestiona licencias de POS de escritorio</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowPlanModal(true)} className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm font-medium transition-colors">
              <Plus className="w-4 h-4" /> Plan
            </button>
            <button onClick={() => setShowDownloadModal(true)} className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm font-medium transition-colors">
              <Upload className="w-4 h-4" /> Versión
            </button>
            <button onClick={() => setShowGenerateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors">
              <KeyRound className="w-5 h-5" /> Generar Serial
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          <button onClick={() => setActiveTab('licenses')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'licenses' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
            Licencias ({total})
          </button>
          <button onClick={() => setActiveTab('plans')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'plans' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
            Planes ({plans.length})
          </button>
          <button onClick={() => setActiveTab('downloads')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'downloads' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
            Versiones ({downloads.length})
          </button>
          <button onClick={() => { setActiveTab('stamps'); if (!stampsDashboard) loadStampsDashboard(); }} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'stamps' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
            <span className="flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" /> Timbres</span>
          </button>
          <button onClick={() => setActiveTab('addon-catalog')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'addon-catalog' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
            <span className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> Catálogo</span>
          </button>
          <button onClick={() => setActiveTab('stamp-packages')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'stamp-packages' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
            <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Paquetes</span>
          </button>
        </div>

        {activeTab === 'licenses' && (
          <>
            {/* Filters */}
            <div className="bg-white p-4 rounded-lg border border-slate-200 flex flex-wrap gap-4 items-end">
              <div className="w-72">
                <label className="block text-xs font-medium text-slate-500 mb-1">Negocio</label>
                <BusinessSearchSelect
                  value={filters.business_id}
                  onChange={(id) => setFilters({ ...filters, business_id: id })}
                  placeholder="Todos los negocios..."
                />
              </div>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500">
                <option value="">Todos los estados</option>
                <option value="pending">Pendiente</option>
                <option value="activated">Activada</option>
                <option value="linked">Ligada</option>
                <option value="expired">Expirada</option>
                <option value="revoked">Revocada</option>
              </select>
              <select value={filters.pos_type} onChange={(e) => setFilters({ ...filters, pos_type: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500">
                <option value="">Todos los tipos</option>
                <option value="retail">Retail</option>
                <option value="restaurant">Restaurante</option>
              </select>
              <select value={filters.product_id} onChange={(e) => setFilters({ ...filters, product_id: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500">
                <option value="">Todos los productos</option>
                {softwareProducts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            {/* Licenses Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Serial</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Negocio</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Producto</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Equipo</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Última conexión</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {licenses.length === 0 ? (
                      <tr><td colSpan="8" className="px-4 py-12 text-center text-slate-500">No hay licencias</td></tr>
                    ) : (
                      licenses.map((lic) => {
                        const st = STATUS_LABELS[lic.status] || { label: lic.status, color: 'bg-slate-100 text-slate-600' };
                        return (
                          <tr key={lic.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <code className="text-sm font-mono text-slate-800">{lic.serial_number}</code>
                                <button onClick={() => copySerial(lic.serial_number)} className="p-1 hover:bg-slate-100 rounded"><Copy className="w-3.5 h-3.5 text-slate-400" /></button>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">{getBusinessName(lic.business_id)}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{lic.product_name || '—'}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                <Monitor className="w-3 h-3" /> {POS_TYPE_LABELS[lic.pos_type] || lic.pos_type}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">{lic.hardware_details?.hostname || '—'}</td>
                            <td className="px-4 py-3 text-sm text-slate-500">{lic.last_seen ? formatDate(lic.last_seen) : '—'}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => navigate(`/superadmin/licencias-pos/${lic.serial_number}/gestion`)} className="p-1.5 hover:bg-teal-50 rounded text-teal-600" title="Gestionar addons y timbres">
                                  <Settings className="w-4 h-4" />
                                </button>
                                <button onClick={() => setShowDetailModal(lic)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 text-xs font-medium">Detalle</button>
                                {lic.status !== 'revoked' && (
                                  <button onClick={() => setShowRevokeModal(lic)} className="p-1.5 hover:bg-red-50 rounded text-red-500" title="Revocar">
                                    <Ban className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === 'plans' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.length === 0 ? (
              <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center">
                <p className="text-slate-500">No hay planes. Crea uno primero.</p>
              </div>
            ) : plans.map((plan) => (
              <div key={plan.id} className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
                <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                <p className="text-sm text-slate-500">{plan.description || 'Sin descripción'}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Tipo:</span>
                  <span className="font-medium">{POS_TYPE_LABELS[plan.pos_type] || plan.pos_type}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Precio:</span>
                  <span className="font-medium">${plan.price_mxn?.toLocaleString('es-MX')} MXN</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Ciclo:</span>
                  <span className="font-medium capitalize">{plan.billing_cycle}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Terminales:</span>
                  <span className="font-medium">{plan.max_terminals || 1}</span>
                </div>
                <p className="text-xs text-slate-400">Código: {plan.code}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'downloads' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {downloads.length === 0 ? (
              <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center">
                <p className="text-slate-500">No hay versiones registradas.</p>
              </div>
            ) : downloads.map((dl) => (
              <div key={dl.id} className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-900">v{dl.version}</h3>
                  <div className="flex gap-2">
                    {dl.is_latest && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Última</span>}
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{POS_TYPE_LABELS[dl.pos_type]}</span>
                  </div>
                </div>
                <p className="text-sm text-slate-500">{dl.release_notes || 'Sin notas'}</p>
                {dl.file_size_mb && <p className="text-xs text-slate-400">{dl.file_size_mb} MB | {dl.platform} | Min: {dl.min_os_version}</p>}
                <a href={dl.download_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 font-medium">
                  <Download className="w-4 h-4" /> Descargar
                </a>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'stamps' && (
          <div className="space-y-4">
            {stampsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
              </div>
            ) : stampsDashboard ? (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-sm text-slate-500">Total disponibles</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{(stampsDashboard.stamps?.total_available || 0).toLocaleString('es-MX')}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-sm text-slate-500">Usados este mes</p>
                    <p className="text-2xl font-bold text-orange-600 mt-1">{(stampsDashboard.stamps?.used_this_month || 0).toLocaleString('es-MX')}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-sm text-slate-500">Recargados este mes</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">{(stampsDashboard.stamps?.recharged_this_month || 0).toLocaleString('es-MX')}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-sm text-slate-500">Licencias en cero</p>
                    <p className="text-2xl font-bold text-red-600 mt-1">{stampsDashboard.stamps?.licenses_at_zero || 0}</p>
                  </div>
                </div>

                {/* Zero-stamp alerts */}
                {stampsDashboard.stamps?.zero_balance_licenses?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      <h3 className="text-sm font-semibold text-slate-700">Licencias sin timbres</h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {stampsDashboard.stamps.zero_balance_licenses.map((lic) => (
                        <div key={lic.serial_number} className="px-6 py-3 flex items-center justify-between hover:bg-slate-50">
                          <div>
                            <code className="text-sm font-mono text-slate-800">{lic.serial_number}</code>
                            <p className="text-xs text-slate-400 mt-0.5">{lic.business_id?.slice(0, 8)}...</p>
                          </div>
                          <button
                            onClick={() => navigate(`/superadmin/licencias-pos/${lic.serial_number}/gestion`)}
                            className="text-xs text-teal-700 hover:text-teal-800 font-medium"
                          >
                            Recargar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Addon stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-sm text-slate-500">Addons activos</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">{stampsDashboard.addons?.active_count || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-sm text-slate-500">Total licencias</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">{stampsDashboard.licenses?.total || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <p className="text-sm text-slate-500">Licencias activas</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">{stampsDashboard.licenses?.active || 0}</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <p className="text-slate-500">No se pudo cargar el dashboard de timbres</p>
                <button onClick={loadStampsDashboard} className="mt-3 text-sm text-teal-700 hover:underline">Reintentar</button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'addon-catalog' && <AddonCatalogTab />}

        {activeTab === 'stamp-packages' && <StampPackagesTab />}
      </div>

      {/* Generate License Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Generar Licencia</h2>
              <button onClick={() => setShowGenerateModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleGenerate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Negocio *</label>
                <BusinessSearchSelect
                  value={generateForm.business_id}
                  onChange={(id) => setGenerateForm({ ...generateForm, business_id: id })}
                  placeholder="Buscar negocio..."
                />
                {!generateForm.business_id && <p className="text-xs text-red-500 mt-1">Selecciona un negocio</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Producto *</label>
                <select required value={generateForm.product_id} onChange={(e) => {
                  const prod = softwareProducts.find(p => p.id === e.target.value);
                  const posType = prod?.legacy_pos_type || generateForm.pos_type;
                  setGenerateForm({ ...generateForm, product_id: e.target.value, pos_type: posType, plan_id: '' });
                }} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
                  <option value="">Selecciona un producto</option>
                  {softwareProducts.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de POS</label>
                <select value={generateForm.pos_type} onChange={(e) => setGenerateForm({ ...generateForm, pos_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
                  <option value="retail">Retail</option>
                  <option value="restaurant">Restaurante</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Plan *</label>
                <select required value={generateForm.plan_id} onChange={(e) => setGenerateForm({ ...generateForm, plan_id: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
                  <option value="">Selecciona un plan</option>
                  {plans.filter((p) => p.pos_type === generateForm.pos_type).map((p) => <option key={p.id} value={p.id}>{p.name} - ${p.price_mxn} MXN ({p.billing_cycle})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notas</label>
                <input type="text" value={generateForm.notes} onChange={(e) => setGenerateForm({ ...generateForm, notes: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="Ej: Licencia para Sucursal Centro" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors">Generar Serial</button>
                <button type="button" onClick={() => setShowGenerateModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Revoke Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Revocar Licencia</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">Esta acción desactivará permanentemente la licencia <strong>{showRevokeModal.serial_number}</strong>. El POS de escritorio dejará de funcionar.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Razón (opcional)</label>
                <input type="text" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500" placeholder="Ej: Cliente dejó de pagar" />
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={handleRevoke} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors">Revocar</button>
                <button onClick={() => { setShowRevokeModal(null); setRevokeReason(''); }} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-slate-900">Detalle de Licencia</h2>
              <button onClick={() => setShowDetailModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg">
                <code className="text-sm font-mono text-slate-800 flex-1">{showDetailModal.serial_number}</code>
                <button onClick={() => copySerial(showDetailModal.serial_number)} className="p-1.5 hover:bg-slate-200 rounded"><Copy className="w-4 h-4 text-slate-600" /></button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Estado</label><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_LABELS[showDetailModal.status]?.color}`}>{STATUS_LABELS[showDetailModal.status]?.label}</span></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Tipo</label><p className="text-sm font-medium text-slate-900">{POS_TYPE_LABELS[showDetailModal.pos_type]}</p></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Producto</label><p className="text-sm text-slate-900">{showDetailModal.product_name || '—'}</p></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Negocio</label><p className="text-sm text-slate-900">{getBusinessName(showDetailModal.business_id)}</p></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Creada</label><p className="text-sm text-slate-900">{formatDate(showDetailModal.created_at)}</p></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Activada</label><p className="text-sm text-slate-900">{formatDate(showDetailModal.activated_at)}</p></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Ligada</label><p className="text-sm text-slate-900">{formatDate(showDetailModal.linked_at)}</p></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Última conexión</label><p className="text-sm text-slate-900">{formatDate(showDetailModal.last_seen)}</p></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Versión POS</label><p className="text-sm text-slate-900">{showDetailModal.pos_version || '—'}</p></div>
              </div>
              {showDetailModal.hardware_details && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-2">Información del Equipo</label>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                    <p><span className="text-slate-500">Hostname:</span> <span className="font-medium">{showDetailModal.hardware_details.hostname || '—'}</span></p>
                    <p><span className="text-slate-500">OS:</span> <span className="font-medium">{showDetailModal.hardware_details.os_version || '—'}</span></p>
                    <p><span className="text-slate-500">CPU:</span> <span className="font-mono text-xs">{showDetailModal.hardware_details.cpu_id || '—'}</span></p>
                    <p><span className="text-slate-500">MAC:</span> <span className="font-mono text-xs">{showDetailModal.hardware_details.mac_address || '—'}</span></p>
                    <p><span className="text-slate-500">Disco:</span> <span className="font-mono text-xs">{showDetailModal.hardware_details.disk_serial || '—'}</span></p>
                  </div>
                </div>
              )}
              {showDetailModal.hardware_fingerprint && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Fingerprint</label>
                  <code className="text-xs font-mono text-slate-500 break-all">{showDetailModal.hardware_fingerprint}</code>
                </div>
              )}
              {showDetailModal.notes && (
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Notas</label><p className="text-sm text-slate-700">{showDetailModal.notes}</p></div>
              )}
              {showDetailModal.revoked_at && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">Revocada: {formatDate(showDetailModal.revoked_at)}</p>
                  {showDetailModal.revoke_reason && <p className="text-sm text-red-600 mt-1">Razón: {showDetailModal.revoke_reason}</p>}
                </div>
              )}
              <div className="pt-2"><p className="text-xs text-slate-400">ID: {showDetailModal.id}</p></div>
            </div>
          </div>
        </div>
      )}

      {/* Create Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Nuevo Plan</h2>
              <button onClick={() => setShowPlanModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleCreatePlan} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                <input type="text" required value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" placeholder="POS Desktop Retail" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Código *</label>
                <input type="text" required value={planForm.code} onChange={(e) => setPlanForm({ ...planForm, code: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" placeholder="pos_retail" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tipo POS</label>
                  <select value={planForm.pos_type} onChange={(e) => setPlanForm({ ...planForm, pos_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500">
                    <option value="retail">Retail</option>
                    <option value="restaurant">Restaurante</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Ciclo</label>
                  <select value={planForm.billing_cycle} onChange={(e) => setPlanForm({ ...planForm, billing_cycle: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500">
                    <option value="perpetual">Perpetuo</option>
                    <option value="monthly">Mensual</option>
                    <option value="yearly">Anual</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Precio MXN</label>
                  <input type="number" step="0.01" value={planForm.price_mxn} onChange={(e) => setPlanForm({ ...planForm, price_mxn: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Terminales permitidas *</label>
                  <input type="number" min="1" required value={planForm.max_terminals} onChange={(e) => setPlanForm({ ...planForm, max_terminals: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                <input type="text" value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors">Crear Plan</button>
                <button type="button" onClick={() => setShowPlanModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-slate-900">Nueva Versión POS</h2>
              <button onClick={() => setShowDownloadModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleCreateDownload} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tipo POS *</label>
                  <select required value={downloadForm.pos_type} onChange={(e) => setDownloadForm({ ...downloadForm, pos_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500">
                    <option value="retail">Retail</option>
                    <option value="restaurant">Restaurante</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Versión *</label>
                  <input type="text" required value={downloadForm.version} onChange={(e) => setDownloadForm({ ...downloadForm, version: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" placeholder="1.0.0" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">URL de Descarga *</label>
                <input type="url" required value={downloadForm.download_url} onChange={(e) => setDownloadForm({ ...downloadForm, download_url: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" placeholder="https://..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tamaño (MB)</label>
                  <input type="number" step="0.1" value={downloadForm.file_size_mb} onChange={(e) => setDownloadForm({ ...downloadForm, file_size_mb: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">OS Mínimo</label>
                  <input type="text" value={downloadForm.min_os_version} onChange={(e) => setDownloadForm({ ...downloadForm, min_os_version: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notas de versión</label>
                <textarea rows={3} value={downloadForm.release_notes} onChange={(e) => setDownloadForm({ ...downloadForm, release_notes: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={downloadForm.is_latest} onChange={(e) => setDownloadForm({ ...downloadForm, is_latest: e.target.checked })} className="rounded border-slate-300" />
                Marcar como última versión
              </label>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors">Registrar</button>
                <button type="button" onClick={() => setShowDownloadModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default LicenciasPOS;
